import React, { useEffect, useRef } from 'react';
import { ViewId, NAV_ITEMS } from '../../types/navigation';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: ViewId;
  onViewChange: (view: ViewId) => void;
}

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  onClose,
  currentView,
  onViewChange,
}) => {
  const activeView = currentView === 'archive' ? 'treatises' : currentView;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus trap, auto-focus, focus restoration, body scroll lock, and Escape handling
  useEffect(() => {
    if (!isOpen) return;

    // 1. Capture previously focused element to restore on close
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    // 2. Lock body scroll
    document.body.style.overflow = 'hidden';

    // 3. Auto-focus the close button upon opening
    closeButtonRef.current?.focus();
    const focusTimer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    // 4. Keyboard trap: Tab, Shift+Tab, and Escape
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        if (!dialogRef.current) return;

        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter(
          (el) =>
            el.tabIndex !== -1 &&
            !el.hasAttribute('disabled') &&
            el.getAttribute('aria-hidden') !== 'true'
        );

        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        const currentIndex = focusables.indexOf(document.activeElement as HTMLElement);

        if (e.shiftKey) {
          if (currentIndex <= 0) {
            focusables[focusables.length - 1].focus();
          } else {
            focusables[currentIndex - 1].focus();
          }
        } else {
          if (currentIndex === -1 || currentIndex >= focusables.length - 1) {
            focusables[0].focus();
          } else {
            focusables[currentIndex + 1].focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);

      // Restore focus to previous element if still attached to DOM
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Mobile Navigation"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-obsidian-deep/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-xs border-l border-iron-dark bg-obsidian-deep p-6 shadow-chthonic flex flex-col justify-between">
        <div>
          {/* Header */}
          <div className="flex items-center justify-between pb-6 border-b border-iron-dark">
            <span className="font-display text-base font-bold tracking-monumental text-gold-ancient uppercase">
              Sanctum Portal
            </span>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded border border-iron-dark text-parchment-dim hover:text-parchment-light focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright focus-visible:border-gold-ancient transition-colors"
              aria-label="Close menu"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
                <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="mt-6 flex flex-col gap-2" aria-label="Mobile Views">
            {NAV_ITEMS.map((item) => {
              const isActive = activeView === item.id || (item.id === 'treatises' && currentView === 'archive');
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onViewChange(item.id);
                    onClose();
                  }}
                  className={`flex w-full items-center justify-between rounded px-4 py-3.5 text-left font-display tracking-wide transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright/60 focus-visible:border-gold-ancient ${
                    isActive
                      ? 'border border-bronze-base/50 bg-obsidian-surface text-gold-bright shadow-glow-bronze'
                      : 'border border-transparent text-parchment-dim hover:bg-obsidian-surface/60 hover:text-parchment-light'
                  }`}
                >
                  <span className="text-sm font-semibold uppercase">{item.label}</span>
                  <span className="text-[10px] font-mono tracking-widest text-iron-steel uppercase">
                    {item.latinSubtitle}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Footer Epigraph */}
        <div className="pt-6 border-t border-iron-dark/60 text-center">
          <p className="font-display text-[10px] tracking-widest text-iron-steel uppercase">
            Beli-'Ol • Without a Yoke
          </p>
          <p className="mt-1 text-[9px] text-parchment-dim/60 font-mono">
            Guild ID: 1526789048335863988
          </p>
        </div>
      </div>
    </div>
  );
};
